/**
 * Shared options for "live" views that must stay fresh at a 1-second cadence
 * while the tab is visible, and stop polling entirely while it is hidden.
 *
 * This is an OPTIONS FACTORY, not a hook, so it composes into both
 * `useQuery` and `useInfiniteQuery` (the group feed and the DM inbox are
 * infinite queries). Always spread it LAST in the query config so nothing
 * silently clobbers `staleTime`.
 *
 * `staleTime: 0` is mandatory, not cosmetic: lib/query-client.ts sets a 60s
 * global staleTime. `refetchInterval` fires regardless of staleness, but
 * refetchOnMount / refetchOnWindowFocus / refetchOnReconnect are ALL gated on
 * it — so a tab that was hidden (where the interval returned false and
 * stopped) would get no refetch on focus while its cache entry is under 60s
 * old. Do not fix that by lowering the global default; other queries rely on
 * it.
 *
 * Note also that `refetchIntervalInBackground: false` alone does NOT pause a
 * backgrounded tab in TanStack v5 — only the `document.visibilityState` check
 * inside the function-form interval does. Both must stay together. The
 * function is re-evaluated after each fetch settles, so hiding a tab stops
 * polling at the NEXT tick (at most one extra request).
 *
 * Opt-in per query. Never make this a global default.
 */
interface LivePollOptions {
  /** Poll cadence in ms while the tab is visible. Default 1000. */
  intervalMs?: number;
  /** Set false to suspend polling entirely. Default true. */
  enabled?: boolean;
}

export function livePollOptions({
  intervalMs = 1_000,
  enabled = true,
}: LivePollOptions = {}) {
  return {
    refetchInterval: () =>
      enabled &&
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden"
        ? intervalMs
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  } as const;
}
